import React, { useEffect, useState, useContext } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

console.log('🚀 AdminLayout module loaded');
import {
  Home,
  FolderOpen,
  Users,
  BarChart,
  Settings,
  Package,
  CalendarDays,
  LogOut,
  Bell,
  Briefcase,
  Receipt,
  Signature,
  Car,
  ClipboardList,
  TrendingUp,
  ClipboardCheck
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import { authFetch } from "../api";

const AdminLayout = () => {
  console.log('🚀 AdminLayout component is rendering!');
  console.log('🚀 AdminLayout - This should appear in console');
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  console.log('🚀 AdminLayout user from context:', user);
  console.log('🚀 AdminLayout user.effectivePermissions:', user?.effectivePermissions);
  console.log('🚀 AdminLayout user.access:', user?.access);
  console.log('🚀 AdminLayout user.role:', user?.role);
  const [hasUnread, setHasUnread] = useState(false);
  const [, setUserAvailable] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };


  useEffect(() => {
    if (user && user.email) {
      setUserAvailable(true);
    }
  }, [user]);

  const handleLogout = async () => {
    try {
      logout();
      navigate('/login');
    } catch (err) {
      console.error("Logout failed", err);
    }
  };

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await authFetch("/notifications");
        // Handle both response structures
        const notifications = res.data || res || [];
        const hasUnreadNotifications = notifications.some(n => !n.read);
        console.log('🔔 Notifications check:', { count: notifications.length, hasUnread: hasUnreadNotifications });
        setHasUnread(hasUnreadNotifications);
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
        setHasUnread(false);
      }
    };
    
    if (user) {
      fetchNotifications();
    }
  }, [user]);

  const navItems = [
    { label: t('adminLayout.home'), icon: <Home size={18} />, path: "/admin/dashboard/home", permissionKey: "home" },
    { label: t('adminLayout.projects'), icon: <FolderOpen size={18} />, path: "/admin/dashboard/projects", permissionKey: "projects" },
    { label: t('adminLayout.assignProjects'), icon: <ClipboardCheck size={18} />, path: "/admin/dashboard/assign", permissionKey: "assignProjects" },
    { label: t('adminLayout.dailyLogs'), icon: <CalendarDays size={18} />, path: "/admin/dashboard/daily-logs", permissionKey: "dailyLogs" },
    { label: t('adminLayout.analytics'), icon: <BarChart size={18} />, path: "/admin/dashboard/analytics", permissionKey: "analytics" },
    { label: t('adminLayout.forecasting'), icon: <TrendingUp size={18} />, path: "/admin/dashboard/forecasting", permissionKey: "forecasting" },
    { label: t('adminLayout.autoPark'), icon: <Car size={18} />, path: "/admin/dashboard/autopark", permissionKey: "autopark" },
    { label: t('adminLayout.inventory'), icon: <ClipboardList size={18} />, path: "/admin/dashboard/inventory", permissionKey: "inventory" },
    { label: t('adminLayout.invoices'), icon: <Receipt size={18} />, path: "/admin/dashboard/invoices", permissionKey: "invoices" },
    { label: t('adminLayout.contracts'), icon: <Signature size={18} />, path: "/admin/dashboard/contracts", permissionKey: "contracts" },
    { label: t('adminLayout.workers'), icon: <Briefcase size={18} />, path: "/admin/dashboard/workers", permissionKey: "workers" },
    { label: t('adminLayout.users'), icon: <Users size={18} />, path: "/admin/dashboard/users", permissionKey: "users" },
    { label: t('adminLayout.resources'), icon: <Package size={18} />, path: "/admin/dashboard/resources", permissionKey: "resources" },
    { label: t('adminLayout.notifications'), icon: <Bell size={18} className={hasUnread ? "text-red-500" : "text-gray-600"} />,
      path: "/admin/dashboard/notifications", permissionKey: "notifications"
    },
    { label: t('adminLayout.settings'), icon: <Settings size={18} />, path: "/admin/dashboard/settings", permissionKey: "settings" }
  ];

  // Debug logging for SuperAdmin
  console.log('🔍 AdminLayout Debug:', {
    user: user ? {
      name: user.name,
      role: user.role,
      effectivePermissions: user.effectivePermissions,
      access: user.access
    } : 'No user',
    navItemsCount: navItems.length
  });
  
  // Debug the user data structure
  console.log('🔍 AdminLayout User Data Structure:', {
    hasEffectivePermissions: !!user?.effectivePermissions,
    hasAccess: !!user?.access,
    effectivePermissionsKeys: user?.effectivePermissions ? Object.keys(user.effectivePermissions) : 'none',
    accessKeys: user?.access ? Object.keys(user.access) : 'none',
    fullUserData: user
  });

  return (
    <div className="flex min-h-screen bg-gray-200 p-6">
      <aside className={`${sidebarCollapsed ? 'w-20' : 'w-64'} bg-white text-gray-700 flex flex-col p-6 rounded-3xl shadow-2xl border border-gray-200 transition-all duration-300`}>
                <div>
          <div className={`flex flex-col items-center mb-8 ${sidebarCollapsed ? 'px-0' : 'px-4'}`}>
            {sidebarCollapsed ? (
              <div className="w-8 h-8 bg-green-800 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">I</span>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-center text-green-800 tracking-tight mb-2">{t('adminLayout.adminPanel')}</h1>
                <div className="w-16 h-1.5 bg-blue-600 rounded-full mb-2" />
              </>
            )}
            
            {/* Language Switcher */}
            {!sidebarCollapsed && (
              <div className="mt-4 mb-2">
                <LanguageSwitcher />
              </div>
            )}
            
            {/* Single Toggle Button with Text */}
            <button
              onClick={toggleSidebar}
              className={`mt-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-2 rounded-lg transition-all duration-200 hover:shadow-md text-sm font-medium ${sidebarCollapsed ? 'w-8 h-8' : 'w-full'}`}
              title={sidebarCollapsed ? t('adminLayout.showSidebar') : t('adminLayout.hideSidebar')}
            >
              {sidebarCollapsed ? (
                <span className="text-xs">→</span>
              ) : (
                t('adminLayout.hideSidebar')
              )}
            </button>
          </div>
          
          {/* Gray Line Separator */}
          {!sidebarCollapsed && (
            <div className="w-full h-px bg-gray-400 mb-6"></div>
          )}
          
          <nav className="space-y-2 mb-6">
            {(() => {
              console.log('🔍 Starting navigation filter with', navItems.length, 'items');
              const filteredItems = navItems.filter((item) => {
                // Use direct permission key from nav item
                const permissionKey = item.permissionKey;
                
                // Use effectivePermissions instead of access
                const effectivePermissions = user?.effectivePermissions || user?.access;
                const hasPageAccess = effectivePermissions?.pages?.[permissionKey] === true;
                
                // Debug logging (remove in production)
                console.log('🔍 Permission check:', {
                  label: item.label,
                  permissionKey,
                  hasPageAccess,
                  userRole: user?.role,
                  effectivePermissions: effectivePermissions?.pages,
                  allPermissionKeys: Object.keys(effectivePermissions?.pages || {})
                });
                
                return hasPageAccess;
              });
              console.log('🔍 Filtered navigation items:', filteredItems.length, 'out of', navItems.length);
              return filteredItems;
            })()
              .map((item) => {
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-3 rounded-xl font-medium text-sm transition-all duration-200 shadow-sm border border-transparent ${
                      location.pathname === item.path 
                        ? "bg-blue-600 text-white border-blue-500 shadow-lg" 
                        : "text-gray-600 hover:bg-blue-100 hover:text-gray-900 hover:shadow-md"
                    }`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    {React.cloneElement(item.icon, { size: sidebarCollapsed ? 20 : 18 })}
                    {!sidebarCollapsed && item.label}
                  </Link>
                );
              })}
          </nav>
          <button
            onClick={handleLogout}
            className={`flex items-center ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3 px-4'} py-3 font-medium text-sm transition-all duration-200 text-gray-600 hover:text-white hover:bg-red-600 hover:shadow-md w-full rounded-xl`}
            title={sidebarCollapsed ? t('adminLayout.logout') : undefined}
          >
            <LogOut size={sidebarCollapsed ? 20 : 18} />
            {!sidebarCollapsed && t('adminLayout.logout')}
          </button>
        </div>
      </aside>
      <main className="flex-1 bg-white p-8 overflow-auto min-h-screen rounded-3xl shadow-2xl border border-gray-200 ml-6">
        <div className="absolute top-6 right-10">
          <NotificationBell />
        </div>
        <Outlet />
      </main>
    </div>
  );
};

console.log('🚀 AdminLayout component defined');

export default AdminLayout;